const sharedTeam = {
  heading: 'Built by people who build together.',
  body: 'Meet the engineers, leaders, and teammates who bring curiosity, ownership, and technical excellence to every mission.',
};

const capabilityDefinitions = [
  {
    number: '01',
    category: 'Engineering',
    slug: 'software-engineering',
    title: 'Software Engineering',
    heroLabel: 'The Builders',
    summary: 'Building reliable, scalable software that powers mission-critical work.',
    heroSubtitle: 'Building software that matters starts with building the right team.',
    introduction: [
      'From mission-critical government systems to modern cloud applications, Reckon engineers design and build software that solves complex problems, delivers lasting value, and supports the missions our customers depend on.',
      "Our success isn't defined by the technologies we use. It's defined by the people behind them.",
    ],
    whyItMatters: [
      "Software engineering is more than writing code. It's understanding the mission, asking the right questions, and building solutions that continue to deliver value long after deployment.",
      "At Reckon, our engineers become trusted members of our customers' teams. We work collaboratively, communicate openly, and approach every challenge with curiosity, ownership, and a commitment to excellence.",
      "Whether we're developing new applications, modernizing legacy systems, or extending existing platforms, we focus on creating software that is secure, maintainable, and built to evolve.",
    ],
    services: [
      'Application Development',
      'Full-Stack Engineering',
      'System Modernization',
      'Cloud-Native Development',
      'API & Systems Integration',
    ],
    delivery: [
      'Technology is only part of the equation.',
      'Our engineers work alongside customers as collaborative partners, bringing technical expertise, accountability, and a shared commitment to success. We believe the best solutions come from understanding the people behind the problem just as well as the technology itself.',
      'That means asking thoughtful questions, embracing continuous learning, and remaining adaptable as missions evolve.',
    ],
    approach: [
      'Understand the mission, the people using the system, and the outcomes that matter most.',
      'Design secure, pragmatic architectures that balance delivery with long-term adaptability.',
      'Build in small, testable increments alongside customer teams.',
      'Share knowledge and strengthen the team throughout delivery.',
    ],
    technologies: [
      'JavaScript & TypeScript',
      'Java & .NET',
      'Python',
      'Vue & React',
      'REST & event-driven APIs',
      'AWS & Azure',
      'Containers & Kubernetes',
      'Relational & NoSQL data stores',
    ],
    engineeringInAction: {
      heading: 'Learning becomes part of the work.',
      body: 'Our engineers bring lessons from hands-on delivery, technical communities, and industry conferences back to their teams. That continuous exchange helps good ideas travel and keeps our solutions ready for what comes next.',
    },
    whyReckon: {
      heading: 'Exceptional software begins with exceptional people.',
      paragraphs: [
        "What sets Reckon apart isn't a particular programming language or framework. It's the way we build teams.",
        'We invest in our engineers through mentorship, continuous learning, industry conferences, and shared experiences that strengthen communication and collaboration.',
        'The result is a team that solves problems together, supports one another, and delivers exceptional outcomes for our customers.',
      ],
    },
    meetTeam: sharedTeam,
    finalCta: {
      heading: 'Ready to Build Together?',
      body: "Whether you're modernizing an existing system, developing a new application, or expanding your engineering team, Reckon is ready to help. Let's build software that supports your mission today and evolves with you tomorrow.",
      primary: 'Start a Conversation',
      secondary: 'Explore Engineering Capabilities',
    },
  },
  {
    number: '02',
    category: 'Engineering',
    slug: 'systems-engineering',
    title: 'Systems Engineering',
    heroLabel: 'The Architects',
    summary: 'Transforming complex requirements into integrated, mission-focused solutions.',
    heroSubtitle: 'Connecting people, requirements, and technology across the system lifecycle.',
    introduction: [
      'Reckon helps teams understand complex systems, translate mission needs into clear technical direction, and keep every component working toward the same outcome.',
    ],
    whyItMatters: [
      'Complex missions depend on more than individual technologies. They require a shared understanding of requirements, interfaces, risks, and how the entire system behaves over time.',
    ],
    services: [
      'Requirements Engineering',
      'System Architecture',
      'Systems Integration',
      'Verification & Validation',
      'Technical Reviews',
      'Lifecycle Engineering',
      'Model-Based Systems Engineering',
    ],
    delivery: [
      'Our systems engineers create clarity across disciplines, connect technical decisions to mission outcomes, and maintain traceability as requirements and environments evolve.',
    ],
    approach: [
      'Translate mission needs into clear, testable requirements.',
      'Make interfaces, dependencies, and risks visible early.',
      'Validate decisions across the full system lifecycle.',
    ],
    technologies: ['SysML', 'Cameo', 'DOORS', 'Jira', 'Confluence', 'Architecture frameworks'],
    engineeringInAction: {
      heading: 'Clarity across the whole system.',
      body: 'Reckon systems engineers bring stakeholders and technical teams together around shared models, traceable decisions, and evidence that the solution meets the mission need.',
    },
    whyReckon: {
      heading: 'Technical depth with a collaborative mindset.',
      paragraphs: [
        'We pair disciplined systems thinking with open communication, helping teams navigate complexity without losing sight of the people and mission behind the requirements.',
      ],
    },
    meetTeam: sharedTeam,
  },
  {
    number: '03',
    category: 'Engineering',
    slug: 'cloud-solutions',
    title: 'Cloud Solutions',
    heroLabel: 'The Cloudbreakers',
    summary: 'Designing secure cloud environments that scale with your mission.',
    heroSubtitle: 'Cloud platforms designed for secure, resilient mission delivery.',
    introduction: [
      'Reckon helps teams plan, build, and evolve cloud platforms that are resilient, governable, and ready for mission workloads.',
    ],
    whyItMatters: [
      'Cloud adoption creates value when the platform is secure, operable, and shaped around the work it supports, not simply when infrastructure moves.',
    ],
    services: [
      'Cloud Architecture & Migration Planning',
      'Landing Zones & Platform Engineering',
      'Infrastructure as Code',
      'Cloud Security & Governance',
      'Reliability & Cost Optimization',
    ],
    delivery: [
      'We align platform decisions with mission, security, and operational needs, then automate repeatable infrastructure and governance controls.',
    ],
    approach: [
      'Design around mission and operational needs.',
      'Automate infrastructure and governance controls.',
      'Build for resilience, observability, and responsible cost management.',
    ],
    technologies: ['AWS', 'Azure', 'Terraform', 'CloudFormation', 'Docker', 'Kubernetes'],
    engineeringInAction: {
      heading: 'Cloud knowledge shared across the team.',
      body: 'Hands-on delivery and continuing education help our engineers turn new cloud capabilities into practical improvements for the teams and missions they support.',
    },
    whyReckon: {
      heading: 'A cloud partner focused on the outcome.',
      paragraphs: [
        'We balance platform expertise with the operational context needed to build cloud environments teams can confidently use and sustain.',
      ],
    },
    meetTeam: sharedTeam,
  },
  {
    number: '04',
    category: 'Delivery',
    slug: 'devsecops',
    title: 'DevSecOps',
    heroLabel: 'The Accelerators',
    summary: 'Accelerating delivery through automation, security, and continuous integration.',
    heroSubtitle: 'Making secure delivery faster, repeatable, and visible.',
    introduction: [
      'Reckon brings development, security, and operations together so teams can release useful software more frequently and with greater confidence.',
    ],
    whyItMatters: [
      'Delivery pipelines are part of the product. When they are reliable and secure, teams spend less time on manual handoffs and more time solving mission problems.',
    ],
    services: [
      'CI/CD Pipeline Engineering',
      'Security Automation & Policy as Code',
      'Container & Artifact Management',
      'Release & Environment Automation',
      'Platform Observability',
    ],
    delivery: [
      'We automate the path from source control to production, move security feedback closer to developers, and continuously remove friction from delivery.',
    ],
    approach: [
      'Automate repeatable delivery work.',
      'Integrate security feedback throughout development.',
      'Measure delivery health and improve continuously.',
    ],
    technologies: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'SonarQube', 'Docker', 'Kubernetes'],
    engineeringInAction: {
      heading: 'Faster feedback, stronger teams.',
      body: 'Our engineers make delivery health visible and use that feedback to help development, security, and operations teams improve the system together.',
    },
    whyReckon: {
      heading: 'Automation built around the people using it.',
      paragraphs: [
        'We focus on dependable workflows and usable feedback so automation supports the team instead of becoming another system to work around.',
      ],
    },
    meetTeam: sharedTeam,
  },
  {
    number: '05',
    category: 'Delivery',
    slug: 'quality-engineering',
    title: 'Quality Engineering',
    heroLabel: 'The Guardians',
    summary: 'Delivering confidence through thoughtful testing, automation, and continuous quality.',
    heroSubtitle: 'Building confidence into every stage of delivery.',
    introduction: [
      'Reckon makes quality a shared engineering discipline, using thoughtful automation and early feedback to reduce risk across the delivery lifecycle.',
    ],
    whyItMatters: [
      'Quality cannot be inspected in at the end. Building it into design and delivery helps teams find issues sooner and release with confidence.',
    ],
    services: [
      'Test Strategy & Planning',
      'Automated Functional & Integration Testing',
      'Performance & Resilience Testing',
      'Accessibility Testing',
      'Quality Metrics & Release Readiness',
    ],
    delivery: [
      'We define quality around user and mission outcomes, balance automated checks with focused exploratory testing, and make feedback fast and actionable.',
    ],
    approach: [
      'Define quality around mission outcomes.',
      'Build testing into the delivery lifecycle.',
      'Make quality feedback visible and actionable.',
    ],
    technologies: ['Cypress', 'Playwright', 'Selenium', 'JUnit', 'Postman', 'JMeter'],
    engineeringInAction: {
      heading: 'Quality as a team habit.',
      body: 'Our quality engineers work alongside delivery teams to turn risks, user needs, and system behavior into feedback everyone can understand and act on.',
    },
    whyReckon: {
      heading: 'Confidence grounded in collaboration.',
      paragraphs: [
        'We treat quality as shared ownership, helping the entire team learn sooner and make stronger release decisions.',
      ],
    },
    meetTeam: sharedTeam,
  },
  {
    number: '06',
    category: 'Delivery',
    slug: 'program-delivery',
    title: 'Program Delivery',
    heroLabel: 'The Coordinators',
    summary: 'Leading complex technical efforts with disciplined execution, transparency, and mission focus.',
    heroSubtitle: 'Turning complex plans into visible, measurable mission progress.',
    introduction: [
      'Reckon gives complex technical programs the clarity, rhythm, and leadership needed to move from strategy to dependable execution.',
    ],
    whyItMatters: [
      'Successful programs connect priorities, people, risks, and delivery decisions. Teams need transparent communication and a shared understanding of what progress means.',
    ],
    services: [
      'Program Management',
      'Project Management',
      'Risk Management',
      'Schedule Management',
      'Stakeholder Communication',
      'Agile Coaching',
      'Scrum Leadership',
    ],
    delivery: [
      'We make progress, risks, and decisions visible while adapting delivery practices to the team and mission instead of following ceremony for its own sake.',
    ],
    approach: [
      'Connect day-to-day delivery with mission outcomes.',
      'Make priorities, risks, and decisions transparent.',
      'Build a delivery rhythm teams can sustain.',
    ],
    technologies: ['Scrum', 'Kanban', 'Jira', 'Confluence', 'Azure DevOps', 'Miro'],
    engineeringInAction: {
      heading: 'Progress the whole team can see.',
      body: 'Our delivery leaders create shared context across technical teams and stakeholders so decisions happen sooner and everyone understands where the mission stands.',
    },
    whyReckon: {
      heading: 'Leadership that strengthens the team.',
      paragraphs: [
        'We bring structure without unnecessary process, helping teams communicate honestly, adapt quickly, and stay accountable to meaningful outcomes.',
      ],
    },
    meetTeam: sharedTeam,
  },
  {
    number: '07',
    category: 'Innovation',
    slug: 'data-ai',
    title: 'Data & AI',
    heroLabel: 'The Signal Finders',
    summary: 'Turning data into meaningful insight through analytics and artificial intelligence.',
    heroSubtitle: 'Trusted data and responsible intelligence for better mission decisions.',
    introduction: [
      'Reckon helps organizations make data usable, discoverable, and trustworthy, then applies analytics and AI where they can create meaningful mission value.',
    ],
    whyItMatters: [
      'Useful intelligence depends on reliable data, clear governance, and solutions grounded in real decisions rather than technology alone.',
    ],
    services: [
      'Data Platform & Pipeline Engineering',
      'Analytics & Visualization',
      'Machine Learning Engineering',
      'Generative AI Applications',
      'Data Governance & Quality',
    ],
    delivery: [
      'We begin with a concrete decision, workflow, or mission need, build governed data foundations, and evaluate AI systems for usefulness and responsible operation.',
    ],
    approach: [
      'Start with the decision or mission need.',
      'Build trusted, governed data foundations.',
      'Evaluate intelligence for usefulness, risk, and responsible operation.',
    ],
    technologies: ['Python', 'SQL', 'Databricks', 'Snowflake', 'Power BI', 'AWS AI services'],
    engineeringInAction: {
      heading: 'Intelligence grounded in real work.',
      body: 'Our engineers connect data and AI capabilities to the decisions teams actually need to make, then measure whether the solution is useful, trustworthy, and ready to operate.',
    },
    whyReckon: {
      heading: 'Curiosity guided by responsibility.',
      paragraphs: [
        'We pair modern data and AI skills with disciplined engineering and a clear focus on the people, risks, and mission outcomes behind the technology.',
      ],
    },
    meetTeam: sharedTeam,
  },
];

export const capabilities = capabilityDefinitions.map((capability) => ({
  ...capability,
  description: capability.summary,
  route: `/capabilities/${capability.slug}`,
}));

export const capabilitiesBySlug = capabilities.reduce((catalog, capability) => ({
  ...catalog,
  [capability.slug]: capability,
}), {});

const capabilityGroupDescriptions = {
  Engineering: 'Designing, building, and evolving modern software systems.',
  Delivery: 'Delivering reliable software through disciplined execution.',
  Innovation: 'Helping customers unlock the value of emerging technologies.',
};

export const capabilityGroups = ['Engineering', 'Delivery', 'Innovation'].map((title) => ({
  id: title.toLowerCase(),
  title,
  description: capabilityGroupDescriptions[title],
  capabilities: capabilities.filter((capability) => capability.category === title),
}));

export const engineeringPrinciples = [
  {
    title: 'Mission First',
    body: 'We begin by understanding the mission before writing code.',
  },
  {
    title: 'Built Together',
    body: 'Collaboration, trust, and shared ownership drive every engagement.',
  },
  {
    title: 'Always Learning',
    body: 'We continuously invest in our people so they can deliver the best solutions.',
  },
];

export const engineeringLifecycle = [
  {
    title: 'Plan',
    description: 'Align the mission, outcomes, constraints, and path forward.',
  },
  {
    title: 'Design',
    description: 'Shape secure, scalable systems around real needs.',
  },
  {
    title: 'Build',
    description: 'Turn thoughtful architecture into maintainable software.',
  },
  {
    title: 'Test',
    description: 'Create confidence through early, continuous feedback.',
  },
  {
    title: 'Deploy',
    description: 'Release safely through automation and disciplined delivery.',
  },
  {
    title: 'Support',
    description: 'Operate, observe, and sustain the systems missions depend on.',
  },
  {
    title: 'Evolve',
    description: 'Learn from the work and adapt as the mission changes.',
  },
];

export const capabilityRoute = (capability) => ({
  name: 'capability-detail',
  params: { capabilitySlug: capability.slug },
});
